import { useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from '@/components/play'
import { isViewer } from '@/components/auth/isViewer'
import { useAuth } from '@/state/AuthContext'

/**
 * Leaving an event's workspace once this account no longer manages it (docs/features/event-managers.md): after its own
 * Leave, or when an organizer removes it (the workspace channel's `access.revoked`, a list of managers without it, or
 * a refused subscription after the socket comes back). It says so, loads the account as it is now, then opens its
 * events, or home for an account that is a viewer again, so no page in between asks for a host account. Once per
 * workspace: a removal noticed several ways leaves once, and losing access during your own Leave is expected.
 */
export function useWorkspaceExit() {
  const { refreshUser } = useAuth()
  const navigate = useNavigate()
  const exiting = useRef(false)
  const leaving = useRef(false)

  const exit = useCallback(
    async (title: string) => {
      if (exiting.current) return
      exiting.current = true
      toast({ title })
      const account = await refreshUser()
      navigate(account && isViewer(account.organization, account.user) ? '/' : '/app', { replace: true })
    },
    [navigate, refreshUser],
  )

  return useMemo(
    () => ({
      /** Your own Leave is under way (true) or failed (false). */
      leaving: (underway: boolean) => {
        leaving.current = underway
      },
      /** Your own Leave went through. */
      left: (eventName: string) => exit(`You left ${eventName}`),
      /** An organizer took this account's access away. */
      lost: (eventName: string) => {
        if (!leaving.current) void exit(`You no longer manage ${eventName}`)
      },
    }),
    [exit],
  )
}

export type WorkspaceExit = ReturnType<typeof useWorkspaceExit>
